// src/lib/emailDomainPolicy.js
// Email domain policy engine for signup validation.
//
// Categories:
// - BLOCKED_EMAIL_DOMAINS:
//     Personal/free email providers, ISP mailboxes, disposable mail services,
//     and other domains that are never allowed.
//
// - REVIEW_EMAIL_DOMAINS:
//     Institutional or competitor domains that require manual approval.
//
// Usage:
//
// import evaluateEmailDomain from "lib/emailDomainPolicy";
//
// const result = evaluateEmailDomain(email);
//
// if (!result.allowed && result.reason === "blocked_domain") {
//   ...
// }
//
// if (!result.allowed && result.reason === "manual_review_required") {
//   ...
// }

export const BLOCKED_EMAIL_DOMAINS = new Set([
  // Major webmail
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "ymail.com",
  "yahoo.co.uk",
  "aol.com",
  "hotmail.com",
  "outlook.com",
  "outlook.co",
  "live.com",
  "msn.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "protonmail.com",
  "proton.me",
  "zoho.com",
  "mail.com",
  "gmx.com",
  "fastmail.com",
  "hushmail.com",
  "tutanota.com",

  // Telecom / ISP / legacy provider domains (personal)
  "att.net",
  "sbcglobal.net",
  "bellsouth.net",
  "pacbell.net",
  "ameritech.net",
  "swbell.net",
  "flash.net",
  "prodigy.net",
  "verizon.net",
  "comcast.net",
  "xfinity.com",
  "charter.net",
  "spectrum.net",
  "cox.net",
  "rr.com", // RoadRunner
  "roadrunner.com",
  "frontier.com",
  "frontiernet.net",
  "optimum.net",
  "centurylink.net",
  "q.com", // CenturyLink alias
  "embarqmail.com",
  "windstream.net",
  "earthlink.net",
  "peoplepc.com",
  "hotmail.co.uk",

  // Common disposable / temporary email providers
  "10minutemail.com",
  "10minutemail.net",
  "10minutemail.co.uk",
  "10minutemail.org",
  "temp-mail.org",
  "temp-mail.com",
  "temp-mail.io",
  "tempail.com",
  "tempmail.net",
  "tempmail.org",
  "tempmail.io",
  "mailinator.com",
  "mailinator.net",
  "mailinator.org",
  "mailinator.co",
  "mailinator.cc",
  "maildrop.cc",
  "maildrop.xyz",
  "maildrop.org",
  "yopmail.com",
  "yopmail.net",
  "yopmail.fr",
  "guerrillamail.com",
  "guerrillamail.net",
  "guerrillamail.biz",
  "guerillamail.com",
  "dispostable.com",
  "dispostable.org",
  "trashmail.com",
  "trashmail.net",
  "trashmail.org",
  "throwawaymail.com",
  "throwawaymail.net",
  "throwawaymail.org",
  "burnermail.io",
  "getnada.com",
  "nada.email",
  "nada.ltd",
  "mailnesia.com",
  "tempinbox.com",
  "spamgourmet.com",
  "spamtempmail.com",
  "spambox.us",
  "spambox.eu",
  "10minutemail.de",
  "moakt.com",
  "sharklasers.com",
  "mailcatch.com",
  "fakeinbox.com",
  "mytemp.email",
  "trashmail.de",
  "0mail.com",
  "dropmail.me",
  "disposablemail.com",
  "pokemail.net",
  "anonymbox.com",
  "guerrillamailblock.com",

  // Other consumer / lesser-known public providers
  "hotmail.fr",
  "live.co.uk",
  "inbox.com",
  "inbox.lv",
  "lycos.com",
  "rocketmail.com",
  "yandex.com",
  "yandex.ru",
  "bk.ru",
  "list.ru",
  "mail.ru",
  "inbox.ru",
  "qq.com",
  "163.com",
  "126.com",
  "sina.com",
  "sohu.com",
  "yeah.net",

  // Generic placeholders often used by disposable providers
  "temporarymail.com",
  "tempmailbox.com",
  "tempmailaddress.com",
]);

export const REVIEW_EMAIL_DOMAINS = new Set([
  // Public finance / municipal advisory / underwriting firms
  "pfm.com",
  "pfmam.com",
  "pragadvisors.com",
  "hilltopsecurities.com",
  "hilltop.com",
  "stifel.com",
  "rwbaird.com",
  "baird.com",
  "fticonsulting.com",
  "bakertilly.com",
  "montgomerysecurities.com",
  "fhnfinancial.com",
  "raymondjames.com",
  "jefferies.com",
  "loopcapital.com",
  "estradahinojosa.com",
  "samco.com",
  "specialdistrictsgroup.com",
  "columbiacapital.com",
  "blxd.com",

  // Banks / swap counterparties
  "jpmorgan.com",
  "chase.com",
  "bofa.com",
  "bankofamerica.com",
  "wellsfargo.com",
  "citi.com",
  "citigroup.com",
  "goldmansachs.com",
  "gs.com",
  "morganstanley.com",
  "ubs.com",
  "barclays.com",
  "deutsche-bank.com",
  "db.com",
  "bnymellon.com",
  "pnc.com",
  "usbank.com",
  "truist.com",
  "regions.com",
  "capitalone.com",
  "svb.com",
  "zionsbank.com",
  "amegybank.com",
  "plainscapital.com",
  "frostbank.com",
  "keybank.com",
  "fifththird.com",
  "bmo.com",
  "scotiabank.com",
  "td.com",
  "hsbc.com",

  // Municipal finance / debt management / analytics software providers
  "sscinc.com",   // SS&C / DBC Finance
  "ssctech.com",
  "debtbook.com",
  "munex.com",
  "adaje.com",
  "sympro.com",
  "daracorporation.com",
  "govinvest.com",
  "bondlink.com",
  "opengov.com",
  "cleargov.com",
  "civicplus.com",
  "questica.com",
  "euna.com",
  "centralsquare.com",
  "tylertech.com",
  "granicus.com",

  // Local Government Investment Pools (LGIPs)
  "texpool.com",
  "texstar.org",
  "texasclass.com",
  "texasterm.com",
  "logic.org",
  "flclass.com",
  "fltrust.com",
  "mbia.com",              // Cooperative Liquid Assets Securities System (CLASS)
  "pmafinancial.com",      // PMA / Wisconsin Investment Series
  "pfm.com",               // PFM asset management / LGIP administration
  "publictrustadvisors.com",
  "governmentportfolioadvisors.com",
  "cutwater.com",
  "mept.org",
  "nmfa.net",
  "michiganclass.org",
  "investinpa.com",
  "investinaim.com",
  "trustarbank.com",
  "classfund.org",
  "ipohio.org",
  "mbslp.org",
  "coopfundingsolutions.com",

    // Municipal-focused investment advisors / public funds managers
  "publictrustadvisors.com",
  "governmentportfolioadvisors.com",
  "pmafinancial.com",
  "pfm.com",
  "pfmam.com",
  "cutwater.com",
  "threeplusone.us",
  "blaylock.com",
  "fundevaluationgroup.com",
  "fortwashington.com",
  "northslopecap.com",
  "gsam.com",                 // Goldman Sachs Asset Management
  "blackrock.com",
  "invesco.com",
  "vanguard.com",
  "fidelity.com",
  "allspringglobal.com",
  "federatedhermes.com",
  "publicfinancepartners.com",
  "meederinvestment.com",
  "frostbank.com",
  "zionsbank.com",
  "texpool.com",
  "texstar.org",
  "texasclass.com",
  "texasterm.com",
  "logic.org",

  // Market data / trading / fintech
  "bloomberg.net",
  "bloomberg.com",
  "lseg.com",
  "refinitiv.com",
  "tradeweb.com",
  "marketaxess.com",
  "ice.com",
  "intercontinentalexchange.com",
  "cmegroup.com",
  "factset.com",
  "spglobal.com",
  "kbra.com",
  "moodys.com",
  "fitchratings.com",

  // Consulting / accounting / muni ecosystem
  "deloitte.com",
  "pwc.com",
  "ey.com",
  "kpmg.com",
  "grantthornton.com",
  "eidebailly.com",
  "forvismazars.com",
  "crowe.com",

  // Bond counsel / law firms
  "bracewell.com",
  "orrick.com",
  "kutakrock.com",
  "nabors.com",
  "mphlegal.com",
  "lockelord.com",
  "hunton.com",
]);

export function normalizeDomain(domain = "") {
  return String(domain)
    .toLowerCase()
    .trim()
    .replace(/^www\./, "");
}

export function extractEmailDomain(email = "") {
  const parts = String(email).toLowerCase().trim().split("@");

  if (parts.length !== 2) {
    return null;
  }

  return normalizeDomain(parts[1]);
}

export function evaluateEmailDomain(email = "") {
  const domain = extractEmailDomain(email);

  if (!domain) {
    return {
      allowed: false,
      reason: "invalid_email",
      domain: null,
    };
  }

  if (BLOCKED_EMAIL_DOMAINS.has(domain)) {
    return {
      allowed: false,
      reason: "blocked_domain",
      domain,
    };
  }

  if (REVIEW_EMAIL_DOMAINS.has(domain)) {
    return {
      allowed: false,
      reason: "manual_review_required",
      domain,
    };
  }

  return {
    allowed: true,
    reason: null,
    domain,
  };
}

export default evaluateEmailDomain;